import React, { useMemo } from "react"
import { Table, Tag, Space, Typography, Result } from "@mimber/ui-web"
import { DollarOutlined, ShoppingOutlined } from "@mimber/ui-web"
import { useMemberOrders } from "@mimber/client/mimber-organization"
import type { ColumnsType, TableProps } from "@mimber/ui-web"
import type { OrderWithRelationsDto } from "@mimber/schema"
import { OrderStatus, OrderSourceType } from "@mimber/schema"
import { formatCurrency } from "@/utils"
import type { MemberOrdersTableProps } from "./MemberOrdersTable.types"
import { SOURCE_LABELS, STATUS_COLORS } from "../OrdersTable/OrdersTable.utils"
import {
  handleTableChange,
  applyQueryParamsToColumns,
  getColumnSearchProps,
  formatTableDate,
} from "@mimber/ui-web"
import { useRememberedTableQueryParams } from "@/core/hooks"
import {
  getRememberedPaginationConfig,
  getRememberedTableLocale,
} from "@/utils"

const { Text } = Typography

export const MemberOrdersTable: React.FunctionComponent<
  MemberOrdersTableProps
> = ({ memberUuid, onRowClick, className, ...tableProps }) => {
  const { queryParams, setQueryParams, resetQueryParams } =
    useRememberedTableQueryParams({
      tableId: "member-orders",
      defaultQueryParams: {
        page: 1,
        limit: 20,
      },
      scope: {
        memberUuid,
      },
    })

  const {
    data: response,
    isLoading,
    error,
  } = useMemberOrders(memberUuid, queryParams)

  const columns: ColumnsType<OrderWithRelationsDto> = useMemo(
    () => [
      {
        title: "Order #",
        dataIndex: "orderNumber",
        key: "orderNumber",
        sorter: true,
        sortDirections: ["ascend", "descend"] as const,
        ...getColumnSearchProps("Search order number"),
        render: (orderNumber: string) => (
          <Space>
            <ShoppingOutlined />
            <Text>{orderNumber}</Text>
          </Space>
        ),
      },
      {
        title: "Source",
        dataIndex: "sourceType",
        key: "sourceType",
        render: (sourceType: OrderSourceType) => (
          <Text>{SOURCE_LABELS[sourceType] ?? sourceType}</Text>
        ),
        filters: Object.values(OrderSourceType).map((type) => ({
          text: SOURCE_LABELS[type] ?? type,
          value: type,
        })),
      },
      {
        title: "Amount",
        key: "totalAmount",
        sorter: true,
        sortDirections: ["ascend", "descend"] as const,
        render: (_: unknown, record: OrderWithRelationsDto) => (
          <Space>
            <DollarOutlined />
            <Text>{formatCurrency(record.totalAmount)}</Text>
          </Space>
        ),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: (status: OrderStatus) => (
          <Tag color={STATUS_COLORS[status] ?? "default"}>{status}</Tag>
        ),
        filters: Object.values(OrderStatus).map((status) => ({
          text: status,
          value: status,
        })),
      },
      {
        title: "Created",
        dataIndex: "createdAt",
        key: "createdAt",
        sorter: true,
        sortDirections: ["ascend", "descend"] as const,
        render: (createdAt: string) => (
          <Text type="secondary">{formatTableDate(createdAt)}</Text>
        ),
      },
    ],
    []
  )

  const displayColumns = useMemo(
    () => applyQueryParamsToColumns(columns, queryParams),
    [columns, queryParams]
  )

  const onTableChange: TableProps<OrderWithRelationsDto>["onChange"] = (
    pagination,
    filters,
    sorter
  ) => {
    const tableChangeData = {
      pagination,
      filters: filters as Record<string, string[] | null>,
      sorter: {
        field: Array.isArray(sorter) ? sorter[0]?.field : sorter?.field,
        order: Array.isArray(sorter) ? sorter[0]?.order : sorter?.order,
      } as { field?: string | string[]; order?: "ascend" | "descend" },
    }

    handleTableChange(tableChangeData, setQueryParams, columns)
  }

  const paginationConfig = getRememberedPaginationConfig(
    response?.meta?.pagination?.total || 0,
    queryParams,
    resetQueryParams
  )

  if (error) {
    return (
      <Result
        status="error"
        title="Failed to load member orders"
        subTitle={error.message}
      />
    )
  }

  return (
    <>
      <Table<OrderWithRelationsDto>
        bordered
        columns={displayColumns}
        dataSource={response?.data || []}
        loading={isLoading}
        rowKey="uuid"
        pagination={paginationConfig}
        locale={getRememberedTableLocale(resetQueryParams, queryParams)}
        onChange={onTableChange}
        onRow={(record) => ({
          onClick: () => onRowClick?.(record),
          style: onRowClick ? { cursor: "pointer" } : undefined,
        })}
        className={className}
        {...tableProps}
      />
    </>
  )
}
